import React, { useState } from "react";
import api, { apiError } from "@/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Stethoscope, Plus, X } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full px-3 py-2.5 rounded-xl bg-amber-50 border border-amber-400/30 text-stone-800 text-sm focus:outline-none focus:border-amber-500";
const SUGGESTED = ["تواصل هاتفي مع المنسق لتحديد سبب التأخر", "زيارة ميدانية للمدرسة", "تدريب المنسق على إدخال الفرص وتسجيل المشاركين", "تنفيذ فرصة تطوعية خلال أسبوع", "رفع نسبة مشاركة الطلاب بتسجيل صف كامل"];

export const PlanDialog = ({ schools, defaultSchoolId, onClose, onSaved }) => {
  const [schoolId, setSchoolId] = useState(defaultSchoolId || "");
  const [problem, setProblem] = useState("");
  const [due, setDue] = useState("");
  const [actions, setActions] = useState([""]);
  const [saving, setSaving] = useState(false);
  const setAct = (i, v) => setActions((a) => a.map((x, j) => (j === i ? v : x)));

  const save = async () => {
    const acts = actions.map((t) => t.trim()).filter(Boolean);
    if (!schoolId) return toast.error("اختر المدرسة");
    if (!problem.trim()) return toast.error("اكتب وصف التعثر");
    if (!acts.length) return toast.error("أضف إجراءً علاجياً واحداً على الأقل");
    setSaving(true);
    try {
      await api.post("/my-plans", { school_id: schoolId, problem, due_date: due, actions: acts.map((text) => ({ text, done: false })) });
      toast.success("تم إنشاء الخطة العلاجية");
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="glass border-amber-400/30 text-stone-800 max-w-lg max-h-[92vh] overflow-y-auto" dir="rtl" data-testid="plan-dialog">
        <DialogHeader><DialogTitle className="gold-text font-head flex items-center gap-2"><Stethoscope size={18} /> خطة علاجية لمدرسة متعثرة</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <select data-testid="plan-school" value={schoolId} onChange={(e) => setSchoolId(e.target.value)} className={inputCls}>
            <option value="">اختر المدرسة...</option>
            {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <textarea data-testid="plan-problem" rows={2} maxLength={1000} value={problem} onChange={(e) => setProblem(e.target.value)} placeholder="وصف التعثر وسببه (مثال: لا فرص منفذة منذ أسبوعين)" className={inputCls} />
          <div>
            <div className="text-xs text-stone-500 mb-1">الإجراءات العلاجية</div>
            <div className="space-y-1.5">
              {actions.map((a, i) => (
                <div key={i} className="flex gap-1.5">
                  <input data-testid={`plan-action-${i}`} value={a} maxLength={300} onChange={(e) => setAct(i, e.target.value)} placeholder={`الإجراء ${i + 1}`} className={inputCls} />
                  {actions.length > 1 && <button type="button" data-testid={`plan-action-del-${i}`} onClick={() => setActions((x) => x.filter((_, j) => j !== i))} className="px-2 text-red-600"><X size={16} /></button>}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {SUGGESTED.map((s) => (
                <button key={s} type="button" onClick={() => setActions((a) => (a.length < 20 ? [...a.filter((x) => x.trim()), s] : a))}
                  className="px-2 py-1 rounded-full text-[11px] border border-sky-300/60 bg-sky-50 text-sky-800 hover:bg-sky-100">+ {s}</button>
              ))}
            </div>
            <button type="button" data-testid="plan-add-action" onClick={() => setActions((a) => (a.length < 20 ? [...a, ""] : a))} className="mt-2 text-xs text-amber-700 flex items-center gap-1"><Plus size={13} /> إجراء آخر</button>
          </div>
          <div>
            <div className="text-xs text-stone-500 mb-1">موعد الإغلاق المستهدف</div>
            <input data-testid="plan-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} className={inputCls} />
          </div>
          <button data-testid="plan-save" onClick={save} disabled={saving} className="btn-gold w-full py-2.5 rounded-xl disabled:opacity-60">{saving ? "جارٍ الحفظ..." : "إنشاء الخطة"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlanDialog;
